import axios from 'axios'
import { useState } from 'react'

import { articleApi } from '../services/articleApi'
import type { AttachmentType, FileUploadStatus } from '../types/article.types'
import { getFileSignature, getReadableFileSize } from '../utils/fileUtils'
import { compressImageForUpload } from '../utils/imageCompression'
import type { ApiError } from '../../../shared/types/common'

const MAX_CONCURRENT_UPLOADS = 3

export type InlineImageField = 'problem' | 'solution'

export type PendingInlineImage = {
  field: InlineImageField
  file: File
  temporarySource: string
}

type AttachmentUploadInput = {
  file: File
  attachmentType: AttachmentType
}

async function runWithConcurrency<T>(items: T[], concurrency: number, worker: (item: T) => Promise<void>) {
  const queue = [...items]
  const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
    while (queue.length > 0) {
      const item = queue.shift()
      if (!item) {
        return
      }

      await worker(item)
    }
  })

  await Promise.all(workers)
}

export function useArticleUploads() {
  const [uploadStatuses, setUploadStatuses] = useState<Record<string, FileUploadStatus>>({})

  function updateUploadStatus(file: File, status: FileUploadStatus) {
    const signature = getFileSignature(file)
    setUploadStatuses((current) => ({ ...current, [signature]: status }))
  }

  async function uploadFileDirectly(articleId: string | number, file: File, attachmentType: AttachmentType) {
    const sourceFile = file
    const sourceSignature = getFileSignature(sourceFile)

    try {
      updateUploadStatus(sourceFile, {
        phase: attachmentType === 'image' ? 'compressing' : 'uploading',
        progress: 0,
        originalSize: sourceFile.size,
        message: attachmentType === 'image' ? 'Compressing image' : 'Preparing upload',
      })

      const prepared = attachmentType === 'image'
        ? await compressImageForUpload(sourceFile)
        : { file: sourceFile, compressed: false }
      const uploadFile = prepared.file
      const uploadSizeText = getReadableFileSize(uploadFile.size)
      const originalSizeText = getReadableFileSize(sourceFile.size)

      updateUploadStatus(sourceFile, {
        phase: 'uploading',
        progress: 0,
        originalSize: sourceFile.size,
        uploadSize: uploadFile.size,
        message: prepared.compressed
          ? `Compressed ${originalSizeText} to ${uploadSizeText}`
          : `Uploading ${uploadSizeText}`,
      })

      const uploadUrlResponse = await articleApi.createAttachmentUploadUrl(articleId, {
        original_name: uploadFile.name,
        mime_type: uploadFile.type || 'application/octet-stream',
        attachment_type: attachmentType,
        size_bytes: uploadFile.size,
      })

      await axios.put(uploadUrlResponse.data.upload_url, uploadFile, {
        headers: uploadUrlResponse.data.headers,
        onUploadProgress: (event) => {
          const total = event.total || uploadFile.size
          const progress = total ? Math.round((event.loaded / total) * 100) : 0

          setUploadStatuses((current) => ({
            ...current,
            [sourceSignature]: {
              ...(current[sourceSignature] || {}),
              phase: 'uploading',
              progress,
              originalSize: sourceFile.size,
              uploadSize: uploadFile.size,
            },
          }))
        },
      })

      const completed = await articleApi.completeAttachmentUpload(articleId, {
        original_name: uploadFile.name,
        mime_type: uploadFile.type || 'application/octet-stream',
        attachment_type: attachmentType,
        size_bytes: uploadFile.size,
        object_key: uploadUrlResponse.data.object_key,
      })

      updateUploadStatus(sourceFile, {
        phase: 'complete',
        progress: 100,
        originalSize: sourceFile.size,
        uploadSize: uploadFile.size,
        message: prepared.compressed
          ? `Compressed ${originalSizeText} to ${uploadSizeText}`
          : 'Upload complete',
      })

      return completed.data
    } catch (error) {
      const message = (error as ApiError).response?.data?.message || 'Upload failed'
      updateUploadStatus(sourceFile, {
        phase: 'error',
        progress: 0,
        originalSize: sourceFile.size,
        message,
      })
      throw error
    }
  }

  async function uploadInlineImages(articleId: string | number, pendingInlineImages: PendingInlineImage[]) {
    const sourceMap = new Map<string, string>()
    const uploadedSignatures = new Set<string>()

    try {
      await runWithConcurrency(pendingInlineImages, MAX_CONCURRENT_UPLOADS, async (inlineImage) => {
        const uploaded = await uploadFileDirectly(articleId, inlineImage.file, 'image')
        uploadedSignatures.add(getFileSignature(inlineImage.file))
        sourceMap.set(
          inlineImage.temporarySource,
          articleApi.publicImageUrl(articleId, uploaded.id),
        )
      })
    } catch (error) {
      Object.assign(error as object, { sourceMap, uploadedSignatures })
      throw error
    }

    return { sourceMap, uploadedSignatures }
  }

  async function uploadRegularAttachments(articleId: string | number, uploads: AttachmentUploadInput[]) {
    const uploadedSignatures = new Set<string>()

    try {
      await runWithConcurrency(uploads, MAX_CONCURRENT_UPLOADS, async (upload) => {
        await uploadFileDirectly(articleId, upload.file, upload.attachmentType)
        uploadedSignatures.add(getFileSignature(upload.file))
      })
    } catch (error) {
      Object.assign(error as object, { uploadedSignatures })
      throw error
    }

    return { uploadedSignatures }
  }

  return {
    setUploadStatuses,
    uploadInlineImages,
    uploadRegularAttachments,
    uploadStatuses,
  }
}
